/*
 *   File : listing.js
 *   Author : https://evoqins.com
 *   Description : List all orders of rayi
 *   Integrations : 'react-toastify'
 *   Version : 1.0.0
*/

// custom packages
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// custom components
import { CustomSearchInput, CustomSelectBox, DateRangePicker } from '../../Components/FormElements';
import { PrimaryButton, SecondaryButton, TextButton } from '../../Components/Buttons';
import { CustomDataTable } from '../../Components/DataTable';
import { Chip, CustomTab } from '../../Components/Others';
import { Icon } from '../../Components/Icon';
import { APIService } from '../../Service';
import EmptyScreen from '../../Components/Others/empty-screen';

// custom style
import color from '../../Style/color.scss';
import { filterOrderTypeOptions } from '../OtherTransactions/transaction-table-config';
import { useSelector } from 'react-redux';

const TAB_TITLES = [
    { id: 1, label: 'All' },
    { id: 2, label: 'Goals' }, //1
    { id: 6, label: 'Insta Save' },//5
    { id: 4, label: 'Smart Baskets' },//3
    { id: 5, label: 'Gold Maxx' },//4
    { id: 3, label: 'Self Managed' } //2
];

const shouldShowFailureReason = (row) => {
    if (!row?.failure_reason) {
        return false;
    }

    const statuses = [
        row.order_status,
        row.fp_order_status,
        row.payment_status,
        row.fp_payment_status,
    ];

    return statuses.some((status) => status === 'Failed');
};

const OrderList = () => {
    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);
    const hasOrderDownloadAccess = PROFILE_DATA?.modules?.some(
        (module) => module?.name?.toLowerCase() === "order download"
    );


    const isRendered = useRef(true);

    const { state } = useLocation();
    const navigate = useNavigate();
    const isClearRef = useRef(false);
    const scrollRef = useRef(null);

    const [searchValue, setSearchValue] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTab, setSelectedTab] = useState(-1);
    const [selectedType, setSelectedType] = useState(null);
    const [rayiPaymentStatus, setRayiPaymentStatus] = useState(null);

    const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);

    const [paymentType, setPaymentType] = useState(null);

    const [searched, setSearched] = useState(false);
    const [loader, setLoader] = useState(true);
    const [filterLoader, setFilterLoader] = useState(true);
    const [toggleFilter, setToggleFilter] = useState(false);

    const [checked, setChecked] = useState([]);
    const [orderListData, setOrderListData] = useState({});
    const [orderFilterData, setOrderFilterData] = useState({});

    const [downloadLoader, setDownloadLoader] = useState(false);

    const [orderDate, setOrderDate] = useState(null);

    useEffect(() => {
        if (isClearRef.current === true) {
            setLoader(true);
            if (filterLoader === false) {
                _orderList();
            }
        }
    }, [isClearRef.current]);

    useEffect(() => {
        if (isRendered.current === true) {
            _filterOrder();
        }
        isRendered.current = false;
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, [isRendered]);

    useEffect(() => {
        // setSearchValue("");
        if (selectedTab != -1) {
            _orderList();
            setLoader(true);
        }
    }, [selectedTab]);


    useEffect(() => {
        if (state !== null) {
            setSelectedTab(state.tab_index);
            setCurrentPage(state.current_page);
        } else {
            setSelectedTab(1);
            setCurrentPage(1);
        }
    }, [state]);


    // handle tab change
    const _handleTab = (tab_id) => {
        navigate(`/orders`, {
            state: {
                tab_index: tab_id,
                current_page: 1
            }
        });
        setSelectedOrderStatus(orderFilterData.order_status[0]);
        // setSelectedPaymentStatus(orderFilterData.payment_status[0]);
        setSelectedType(orderFilterData.order_type[0]);
        setRayiPaymentStatus(orderFilterData.payment_status[0]);
        setOrderDate(null);

        if (orderFilterData.payment_type) {
            setPaymentType(orderFilterData.payment_type[0]);
        }

        setToggleFilter(false);
        setSearchValue("");
    };


    // handle clear
    const _handleClear = () => {
        setSelectedType(orderFilterData.order_type[0]);
        setSelectedOrderStatus(orderFilterData.order_status[0]);
        // setSelectedPaymentStatus(orderFilterData.payment_status[0]);
        setOrderDate(null);
        setRayiPaymentStatus(orderFilterData.payment_status[0]);
        setPaymentType(orderFilterData.payment_type[0]);

        navigate(`/orders`, {
            state: {
                tab_index: selectedTab,
                current_page: 1
            }
        });

        // _orderList(1);
        isClearRef.current = true
    };

    // handle table page change 
    const _handleTablePageChange = (page_num) => {

        _orderList(page_num);

        navigate(`/orders`, {
            state: {
                tab_index: selectedTab,
                current_page: page_num
            }
        });
        // setCurrentPage(page_num);

        setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: "smooth", block: 'start' })
            }
        }, 300);
        setLoader(true);
    };

    // handle navigate customer detail 
    const _handleNavigateDetail = (id) => {
        navigate(`/customers/${id}`)

    }

    // handle toggle filter 
    const _handleToggleFilter = () => {
        setToggleFilter(prev => !prev);
    };

    // API - download list
    const _downloadList = () => {
        _downloadOrder();
    };

    // handle filter
    const _handleFilter = () => {
        if (!selectedType && !selectedOrderStatus && !rayiPaymentStatus) {
            toast.dismiss();
            toast.success("Please select at least one filter", { type: "error" });
        } else {
            setLoader(true);
            _orderList(1);
        }
    };

    // handle select filter 
    const _handleSelectFilter = (selected, type) => {
        setCurrentPage(1);
        if (type === 1) {
            setRayiPaymentStatus(selected);
            isClearRef.current = false;
        } else if (type === 2) {
            setSelectedOrderStatus(selected);
            isClearRef.current = false;
        } else if (type === 3) {
            setSelectedType(selected);
            isClearRef.current = false;
        } else {
            setPaymentType(selected);
            isClearRef.current = false;
        }
        isClearRef.current = false;
    };

    // on search
    const _onSearch = () => {
        setSearched(true);
        setLoader(true)
        _orderList(1);
    };

    // handle search clear
    const _handleSearchClear = () => {
        setSearched(false);
        setSearchValue("");
        setLoader(true);
        if (searched === true) {
            _orderList(1, "")
        }
    };


    const _expandableRows = ({ data }) => {

        const bgClass = data._index % 2 === 0 ? 'e-bg-magnolia-dark' : 'e-bg-magnolia-extra-dark';

        if (!data.data || data.data.length === 0) {
            return <div className={`${bgClass} p-3 e-text-black e-primary-font e-font-14 e-font-weight-400 line-height-16px`}>No schemes available.</div>;
        }

        return (
            <div className={`py-3 padding-30px-lr d-flex flex-column gap-12px ${bgClass} `} >
                {data.data.map((child_data, index) => (
                    <div key={index} className='d-flex align-items-center '>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "180px" }} data-column-id="orders_order_date">
                            {child_data.order_date || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "280px" }} data-column-id="orders_customer_name" title={child_data.name || undefined}>
                            {child_data.name || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "160px" }} data-column-id="orders_type" title={child_data.type || undefined}>
                            {child_data.type || "-"}
                        </p>
                        <div style={{ width: "100px" }} data-column-id="orders_order_type">
                            <Chip label={"Child"}
                                textAlign="justify-content-start"
                                className={"bg-transparent e-text-green-crayola e-border-1px-green-crayola "} />
                        </div>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "140px" }} data-column-id="orders_product">
                            {child_data.product || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px pe-1 text-truncate' style={{ width: "240px" }} data-column-id="orders_product_name" title={child_data.product_name || undefined}>
                            {child_data.product_name || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-20px pe-3 text-truncate' style={{ width: "300px" }} data-column-id="orders_scheme" title={child_data.scheme_name || undefined}>
                            {child_data.scheme_name}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "150px" }} data-column-id="orders_order_amount">
                            {child_data.amount_text || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "150px" }} data-column-id="orders_invested_amount">
                            {child_data.purchase_amount_text || "-"}
                        </p>
                        <div style={{ width: "150px" }} data-column-id="orders_payment_status">
                            {child_data.payment_status !== null ?
                                <Chip label={child_data.payment_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.payment_status === "Success" || child_data.payment_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.payment_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.payment_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                :
                                "-"
                            }
                        </div>
                        <div style={{ width: "150px" }} data-column-id="orders_rayi_order_status">
                            {child_data.order_status !== null ?
                                <Chip label={child_data.order_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.order_status === "Success" || child_data.order_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.order_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.order_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                :
                                "-"}
                        </div>
                        <div style={{ width: "150px" }} data-column-id="orders_fp_payment_status">
                            {child_data.fp_payment_status !== null ?
                                <Chip label={child_data.fp_payment_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.fp_payment_status === "Success" || child_data.fp_payment_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.fp_payment_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.fp_payment_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                : "-"}
                        </div>
                        <div style={{ width: "150px" }} data-column-id="orders_fp_order_status">
                            {child_data.fp_order_status !== null ?
                                <Chip label={child_data.fp_order_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.fp_order_status === "Success" || child_data.fp_order_status === "Successful" ? "e-text-green-crayola e-border-1px-green-crayola" :
                                        child_data.fp_order_status === "Failed" ? "e-text-maximum-red e-border-1px-maximum-red" : child_data.fp_order_status === "Pending" ? "e-text-marigold e-border-1px-marigold" : "e-text-bleu-de-france e-border-1px-solid-bleu-de-france"} bg-transparent `} />
                                : "-"}
                        </div>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "280px" }} data-column-id="orders_failure_reason" title={shouldShowFailureReason(child_data) ? (child_data.failure_reason || undefined) : undefined}>
                            {shouldShowFailureReason(child_data) ? child_data.failure_reason : "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "130px" }} data-column-id="orders_payment_type">
                            {child_data.payment_type || "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "140px" }} data-column-id="orders_allocated_units">
                            {child_data.allocated_units !== null ? child_data.allocated_units : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "140px" }} data-column-id="orders_allocated_nav">
                            {child_data.allocated_nav !== null ? child_data.allocated_nav : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "130px" }} data-column-id="orders_nav_date">
                            {child_data.nav_date || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "160px" }} data-column-id="orders_folio" title={child_data.folio_no != null ? String(child_data.folio_no) : undefined}>
                            {child_data.folio_no != null ? child_data.folio_no : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "320px" }} data-column-id="orders_fp_order_id" title={child_data.fp_order_id || undefined}>
                            {child_data.fp_order_id || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate' style={{ width: "130px" }} data-column-id="orders_order_id" title={child_data.order_display_id || undefined}>
                            {child_data.order_display_id || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "130px" }} data-column-id="orders_customer_id">
                            {child_data.customer_display_id || "-"}
                        </p>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "130px" }} data-column-id="orders_allocated_date">
                            {child_data.allocated_date !== null ? child_data.allocated_date : "-"}
                        </p>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "180px" }} data-column-id="orders_status_updated_on">
                            {child_data.order_status_updated_on || "-"}
                        </p>
                    </div>
                ))}
            </div>
        );
    };

    // Order data table column 
    const ALL_DATA = [
        {
            name: 'Order Date',
            selector: row => row.order_date,
            sortable: true,
            format: row => row.order_date,
            width: "180px",
            id: "orders_order_date",
        },
        {
            name: 'Customer Name',
            cell: (row) =>
                <div onClick={(e) => e.stopPropagation()}>
                    {row.name !== null ?
                        <span className='e-text-blue-violet text-decoration-hover-underline cursor-pointer'
                            onClick={() => _handleNavigateDetail(row.customer_id)}>
                            {row.name}
                        </span> : "-"}
                </div>,
            width: "280px",
            id: "orders_customer_name",
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Type',
            selector: row => row.type,
            cell: row => <span className='text-truncate d-block' title={row.type || undefined}>{row.type || "-"}</span>,
            width: "160px",
            id: "orders_type",
        },
        {
            name: 'Order Type',
            cell: row => <Chip label={"Parent"}
                className={"e-bg-green-crayola"} />,
            width: "100px",
            id: "orders_order_type",
        },
        {
            name: 'Product',
            selector: row => row.product,
            width: "140px",
            id: "orders_product",
        },
        {
            name: 'Product Name',
            cell: row => <div className="text-truncate" title={row.product_name || undefined}>{row.product_name || "-"}</div>,
            width: "240px",
            id: "orders_product_name",
        },
        {
            name: 'Scheme',
            cell: row => <div className="text-truncate" title={row.scheme_name || undefined}>
                {row.scheme_name !== null ? row.scheme_name : "-"}
            </div>,
            width: "300px",
            id: "orders_scheme",
        },
        {
            name: 'Order Amount',
            selector: row => row.amount_text || "-",
            width: "150px",
            id: "orders_order_amount",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.amount < b.amount) return -1;
                if (a.amount > b.amount) return 1;
                return 0;
            }
        },
        {
            name: 'Invested Amount',
            selector: row => row.purchase_amount_text || "-",
            width: "150px",
            id: "orders_invested_amount",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.purchase_amount < b.purchase_amount) return -1;
                if (a.purchase_amount > b.purchase_amount) return 1;
                return 0;
            }
        },

        {
            name: 'Rayi Payment Status',
            cell: row => <>
                {row.payment_status !== null ?
                    <Chip label={row.payment_status}
                        className={row.payment_status === "Success" || row.payment_status == "Successful" ? "e-bg-green-crayola" : row.payment_status === "Failed" ? "e-bg-maximum-red" : row.payment_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    :
                    "-"
                }
            </>,
            width: "150px",
            id: "orders_payment_status",
        },
        {
            name: <span className="e-table-th-wrap">Rayi Order<br />Status</span>,
            cell: row => <>
                {row.order_status !== null ?
                    <Chip label={row.order_status}
                        className={row.order_status === "Success" || row.order_status == "Successful" ? "e-bg-green-crayola" : row.order_status === "Failed" ? "e-bg-maximum-red" : row.order_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    : "-"}
            </>,
            width: "160px",
            id: "orders_rayi_order_status",
        },
        {
            name: 'FP Payment Status',
            cell: row => <>
                {row.fp_payment_status ?
                    <Chip label={row.fp_payment_status}
                        className={row.fp_payment_status === "Success" || row.fp_payment_status == "Successful" ? "e-bg-green-crayola" : row.fp_payment_status === "Failed" ? "e-bg-maximum-red" : row.fp_payment_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    :
                    "-"
                }
            </>,
            width: "150px",
            id: "orders_fp_payment_status",
        },
        {
            name: <span className="e-table-th-wrap">FP Order<br />Status</span>,
            cell: row => <>
                {row.fp_order_status ?
                    <Chip label={row.fp_order_status}
                        className={row.fp_order_status === "Success" || row.fp_order_status == "Successful" ? "e-bg-green-crayola" : row.fp_order_status === "Failed" ? "e-bg-maximum-red" : row.fp_order_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />
                    :
                    "-"
                }
            </>,
            width: "160px",
            id: "orders_fp_order_status",
        },
        {
            name: 'Failed Reason',
            cell: row => <div className="text-truncate" title={shouldShowFailureReason(row) ? (row.failure_reason || undefined) : undefined}>{shouldShowFailureReason(row) ? row.failure_reason : "-"}</div>,
            width: "280px",
            id: "orders_failure_reason",
        },
        {
            name: 'Payment Type',
            selector: row => row.payment_type || "-",
            width: "130px",
            id: "orders_payment_type",
        },
        {
            name: 'Allocated Units',
            selector: row => row.allocated_units !== null ? row.allocated_units : "-",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.allocated_units < b.allocated_units) return -1;
                if (a.allocated_units > b.allocated_units) return 1;
                return 0;
            },
            width: "140px",
            id: "orders_allocated_units",
        },
        {
            name: 'Allocated NAV',
            selector: row => row.allocated_nav !== null ? row.allocated_nav : "-",
            width: "140px",
            id: "orders_allocated_nav",
        },
        {
            name: 'NAV Date',
            selector: row => row.nav_date !== null ? row.nav_date : "-",
            width: "130px",
            id: "orders_nav_date",
        },
        {
            name: 'Folio',
            selector: row => row.folio_no || "-",
            cell: row => <span className="text-truncate d-block" title={row.folio_no || undefined}>{row.folio_no || "-"}</span>,
            width: "160px",
            id: "orders_folio",
        },
        {
            name: "FP Order ID",
            selector: row => row.fp_order_id ? row.fp_order_id : "-",
            cell: row => <span className="text-truncate d-block" title={row.fp_order_id || undefined}>{row.fp_order_id ? row.fp_order_id : "-"}</span>,
            width: "320px",
            id: "orders_fp_order_id",
        },
        {
            name: 'Order ID',
            selector: row => row.order_display_id || "-",
            cell: row => <span className="text-truncate d-block" title={row.order_display_id || undefined}>{row.order_display_id || "-"}</span>,
            width: "130px",
            id: "orders_order_id",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.order_id < b.order_id) return -1;
                if (a.order_id > b.order_id) return 1;
                return 0;
            }
        },
        {
            name: 'Customer ID',
            selector: row => row.customer_display_id,
            width: "130px",
            id: "orders_customer_id",
            sortable: true,
            sortFunction: (a, b) => {
                if (a.customer_id < b.customer_id) return -1;
                if (a.customer_id > b.customer_id) return 1;
                return 0;
            }
        },

        {
            name: 'Allocated Date',
            selector: row => row.allocated_date || "-",
            cell: row => <>{row.allocated_date || "-"}</>,
            sortable: true,
            format: row => row.allocated_date,
            width: "130px",
            id: "orders_allocated_date",
        },
        {
            name: 'Order status updated on',
            selector: row => row.order_status_updated_on || "-",
            sortable: true,
            width: "180px",
            id: "orders_status_updated_on",
        },

    ];

    const renameIdsToValues = (data) => {
        const updatedData = {};

        Object.keys(data).forEach((key) => {
            updatedData[key] = data[key].map((item) => {
                // rename only if `id` exists
                if (item.hasOwnProperty('id')) {
                    return {
                        ...item,
                        value: item.id,
                        // remove the original id key
                        id: undefined,
                    };
                }
                return item;
            }).map(({ id, ...rest }) => rest); // remove undefined id
        });

        return updatedData;
    };

    // API - user detail filter
    const _filterOrder = () => {
        let is_post = false;
        let url = 'orders/filter-data';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                const transformedData = renameIdsToValues(response.data);
                const filteredOrderTypes = filterOrderTypeOptions(transformedData.order_type);
                setOrderFilterData({
                    ...transformedData,
                    order_type: filteredOrderTypes,
                });
                setSelectedType(filteredOrderTypes[0]);
                setSelectedOrderStatus(response.data.order_status[0]);
                // setSelectedPaymentStatus(response.data.payment_status[0]);
                setRayiPaymentStatus(response.data.payment_status[0]);
                if (response.data.payment_type) {
                    setPaymentType(response.data.payment_type[0]);
                }

            } else {
                setOrderFilterData([]);
            }
            setFilterLoader(false);
        });
    };


    // API - order list 
    const _orderList = (page = currentPage, search_value = searchValue) => {


        let updated_payment_status = [];
        let updated_order_status = [];
        let updated_type = [];

        if (rayiPaymentStatus !== null && rayiPaymentStatus?.value !== null) {
            updated_payment_status = rayiPaymentStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (selectedOrderStatus !== null && selectedOrderStatus?.value !== null) {
            updated_order_status = selectedOrderStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (selectedType !== null && selectedType?.value !== null) {
            updated_type = selectedType.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }



        const order_start_date = orderDate === null ? null : format(new Date(orderDate[0]), 'dd-MM-yyyy');
        const order_end_date = orderDate === null ? null : format(new Date(orderDate[1]), 'dd-MM-yyyy');

        let is_post = true;
        let url = 'orders/list';
        let request = {
            "query": search_value,
            "page_num": page,
            "page_size": 10,
            "investment_type": selectedTab === 1 ? null : selectedTab - 1,
            "type": updated_type,
            "order_status": updated_order_status,
            "payment_type": paymentType?.value,
            "payment_status": updated_payment_status,
            "order_start_date": order_start_date,
            "order_end_date": order_end_date

        }

        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {

                const updated_data = response.data.orders.map((item, index) => ({
                    ...item,
                    _index: index
                }));

                setOrderListData({
                    ...response.data,
                    orders: updated_data
                });
                // setOrderListData(response.data)
            } else {
                setOrderListData({ orders: [] });
            }
            setLoader(false);
        });
    };

    // API - user detail download
    const _downloadOrder = () => {

        let updated_payment_status = [];
        let updated_order_status = [];
        let updated_type = [];

        if (rayiPaymentStatus !== null && rayiPaymentStatus?.value !== null) {
            updated_payment_status = rayiPaymentStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })

        }

        if (selectedOrderStatus !== null && selectedOrderStatus?.value !== null) {
            updated_order_status = selectedOrderStatus.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }

        if (selectedType !== null && selectedType?.value !== null) {
            updated_type = selectedType.filter(item => item?.value != null).map((value) => {
                return value.value
            })
        }


        const order_start_date = orderDate === null ? null : format(new Date(orderDate[0]), 'dd-MM-yyyy');
        const order_end_date = orderDate === null ? null : format(new Date(orderDate[1]), 'dd-MM-yyyy');

        let is_post = true;
        let url = 'orders/order-download';
        let request = {
            // "order_ids": checked,
            "query": searchValue === "" ? null : searchValue,
            "investment_type": selectedTab === 1 ? null : selectedTab - 1,
            "type": updated_type,
            "order_status": updated_order_status,
            "payment_type": paymentType?.value,
            "payment_status": updated_payment_status,

            "order_start_date": order_start_date,
            "order_end_date": order_end_date
        }

        setDownloadLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                // window.open(response.data.url, '_blank');
                // const url = response.data.url;
                // const link = document.createElement('a');
                // link.href = url;
                // link.download = ''; // Optional: specify a file name
                // document.body.appendChild(link);
                // link.click();
                // document.body.removeChild(link);
                // setChecked([]);

                toast.dismiss();
                toast.success("The document has been sent to your email.", {
                    type: "success",
                });

                setDownloadLoader(false);
            } else {
                toast.dismiss();
                toast.error(response.message, { type: "error" })

                setDownloadLoader(false);
            }
            setDownloadLoader(false);
        })
    };


    return (
        <section className='e-section-height pb-5' >
            <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                    Orders
                </h3>
            </div>

            <div ref={scrollRef} className='padding-52px-lr margin--80px-top '>
                <div className='e-bg-ghost-white border-radius-16 pb-4 '>
                    <div className='padding-32px-lr padding-32px-top  '>

                        {/* Custom tab */}
                        <div className='d-flex'>
                            <CustomTab data={TAB_TITLES}
                                selectedTab={selectedTab}
                                tooltip={false}
                                handleClick={_handleTab} />
                        </div>

                        <div className='pt-4 d-flex align-items-center pb-2 justify-content-end gap-16px'>
                            {hasOrderDownloadAccess &&
                                <SecondaryButton name="Download List"
                                    disabled={loader === true || orderListData?.orders?.length === 0 ? true : false}
                                    className={downloadLoader ? "w-129px h-42px" : ""}
                                    loader={downloadLoader}
                                    onClick={_downloadList} />
                            }

                            {/* Search input */}
                            <CustomSearchInput placeholder="Search with order ID, investor ID, FP Order ID"
                                type="text"
                                value={searchValue}
                                onSearch={() => _onSearch()}
                                onClear={_handleSearchClear}
                                className="w-370px"
                                onSearchClear={() => _handleSearchClear()}
                                onKeyPress={() => _onSearch()}
                                handleChange={(e) => {
                                    setSearchValue(e);
                                    if (e.length === 0) {
                                        _handleSearchClear();
                                    }
                                }
                                }
                            />

                            <div className={`${toggleFilter ? 'e-bg-blue-violet' : ''}  padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer`}
                                onClick={filterLoader === false && _handleToggleFilter}>
                                <Icon icon='filter'
                                    size='1.25rem'
                                    className="d-flex"
                                    color={toggleFilter && color.white} />
                            </div>
                        </div>
                    </div>

                    {/* Filter section */}
                    <div className={`e-filter-content ${toggleFilter ? 'expanded ' : 'collapsed '}    `}>
                        <div className='e-bg-main-white  pt-3 pb-4 padding-32px-lr '>
                            <div className='d-flex justify-content-end'>
                                <TextButton name="Clear all"
                                    onClick={_handleClear}
                                    disabled={isClearRef.current === true}
                                    cursorPointer={isClearRef.current === true ? 'cursor-not-allowed' : 'cursor-pointer'}
                                />
                            </div>
                            <div className='d-flex gap-3 padding-32px-bottom'>

                                {/* Start date */}
                                {/* <CustomDatePicker
                                    label="Order date"
                                    placeholder="Select"
                                    selected={startDate}
                                    type={2}
                                    toggle={startDateToggle}
                                    // maxDate={endDate}
                                    width="w-160px"
                                    handleToggle={_handleToggleStartDate}
                                    handleClose={_handleToggleStartDate}
                                    handleClear={_handleClearStartDate}
                                    handleSelect={_handleSelectStartDate}
                                /> */}

                                <DateRangePicker label="Order date"
                                    placeholder="Select"
                                    value={orderDate}
                                    handleClear={() => setOrderDate(null)}
                                    handleChange={(date) => setOrderDate(date)} />

                                {/* Type select */}
                                <CustomSelectBox title='Rayi Payment Status'
                                    placeholder='Select'
                                    controlWidth='10rem'
                                    options={orderFilterData.payment_status}
                                    isSearchable={false}
                                    isMulti={true}
                                    type={3}
                                    value={rayiPaymentStatus}
                                    onChange={(data) => _handleSelectFilter(data, 1)} />

                                {/* Order status select */}
                                <CustomSelectBox title='Rayi Order Status'
                                    placeholder='Select'
                                    controlWidth='10rem'
                                    options={orderFilterData.order_status}
                                    isSearchable={false}
                                    value={selectedOrderStatus}
                                    isMulti={true}
                                    type={3}
                                    onChange={(data) => _handleSelectFilter(data, 2)} />

                                {/* Type select */}
                                <CustomSelectBox title='Type'
                                    placeholder='Select'
                                    controlWidth='10rem'
                                    options={orderFilterData.order_type}
                                    isSearchable={false}
                                    value={selectedType}
                                    isMulti={true}
                                    type={3}
                                    onChange={(data) => _handleSelectFilter(data, 3)} />

                                {/* Payment status select */}
                                <CustomSelectBox title='Payment Type'
                                    placeholder='Select'
                                    controlWidth='10rem'
                                    options={orderFilterData.payment_type}
                                    isSearchable={false}
                                    value={paymentType}
                                    onChange={(data) => _handleSelectFilter(data, 4)} />
                            </div>

                            <PrimaryButton name='Filter now'
                                paddingBlock='padding-12px-tb padding-44px-lr'
                                disabled={loader === true ? true : false}
                                onClick={_handleFilter} />
                        </div>
                    </div>


                    <div className='pt-1 padding-32px-lr d-flex gap-1 align-items-center justify-content-end '>
                        <div>
                            <img src={require("../../Assets/Image/scroll-animation.gif")}
                                draggable={false}
                                className='e-rotate-270'
                                width={24}
                                height={24}
                                alt='scroll' />
                        </div>
                        <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-12px'>
                            Scroll horizontally for view more information
                        </p>
                    </div>


                    {/* Order list table */}
                    {loader ?
                        <div className='d-flex align-items-center justify-content-center min-vh-50 pt-4'>
                            <img src={require("../../Assets/Image/loader.gif")}
                                draggable={false}
                                width={80}
                                height={80}
                                alt='loader'
                                className='text-center' />
                        </div>
                        :
                        orderListData.orders.length > 0 ?
                            <div className={`e-data-table-wrapper-striped padding-32px- `}>
                                <CustomDataTable columns={ALL_DATA}
                                    data={orderListData.orders}
                                    pagination={true}
                                    paginationPerPage={10}
                                    expandableRowsComponent={_expandableRows}
                                    defaultExpandedRows={(row) => (row.product === "Goals" || row.product === "Basket") ? true : false}
                                    striped
                                    paginationDefaultPage={orderListData.current_page}
                                    paginationTotalRows={orderListData.total_count}
                                    lastPage={orderListData.total_page}
                                    onChangePage={_handleTablePageChange}
                                // rowClick={(e) => _handleNavigate(e.order_id)}
                                />
                            </div>
                            :
                            <EmptyScreen
                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                title='No results found'
                                description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                            />
                    }
                </div>
            </div>
        </section>
    )
}



export default OrderList;
